import * as React from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom'
import { ScrollRestoration } from '@axilon-apps/libs-ui-core'
import { initWebVitals, isIdentityEnabled } from '@axilon-apps/domains-ontology-ui-state'
import { PLANT_HREF } from '@atbash-labs/plantview-links'
import routes, { standaloneRoutes } from './routes'
import {
  authenticationRoutes,
  onboardingRoutes,
  accountSettingsRoutes
} from '@axilon-apps/domains-identity/web'
import { setIdentityHomePath } from '@axilon-apps/domains-identity/shared/core'
import { AuthenticationGuard } from '@axilon-apps/domains-identity/features/authentication'
import operatorRoutes from './operator/routes'
import { Root } from './components/Root/Root'
import { useOperatorView, getOperatorView } from './lib/operator-view-flag'
import initializeMonacoEditor from './lib/monaco-editor-initializer'
import './styles/global.css'
import '@oicl/openbridge-webcomponents/dist/openbridge.css'

/**
 * Lazy-load the operator stylesheet as a raw string and inject it into
 * the document head while the flag is on. We use Vite's `?inline` query
 * suffix so the CSS is bundled as a string we own, rather than as a
 * side-effect import that lives in the page forever. That gives us
 * symmetric mount/unmount, which is required for a live flag flip:
 * when the flag goes back to off, the operator's `body { font-family,
 * background, ... }` declarations must stop applying so the ontology
 * design tokens regain control.
 */
function useOperatorStylesheet(enabled: boolean): void {
  React.useEffect(() => {
    if (!enabled) return

    let styleEl: HTMLStyleElement | null = null
    let cancelled = false

    ;(async () => {
      const mod = (await import('./operator/styles/global.css?inline')) as { default: string }
      if (cancelled) return
      styleEl = document.createElement('style')
      styleEl.dataset.scope = 'operator-view'
      styleEl.textContent = mod.default
      document.head.appendChild(styleEl)
    })()

    return () => {
      cancelled = true
      if (styleEl && styleEl.parentNode) styleEl.parentNode.removeChild(styleEl)
    }
  }, [enabled])
}

/**
 * Root <App />: reads the audience selector, applies the Floor Copilot
 * stylesheet for technicians, and selects the matching module-level router.
 */
function App(): React.JSX.Element {
  const operatorView = useOperatorView()
  const technicianView = operatorView === 'technician'
  // Opt-in via config.js. When identity is not enabled (e.g. older environments
  // with no identity service deployed), skip the login screen, the auth
  // route-guard, and the identity-owned routes entirely so the app boots
  // straight into the ontology UI.
  const identityEnabled = isIdentityEnabled()

  useOperatorStylesheet(technicianView)

  const router = React.useMemo(() => {
    const appRoutes = technicianView
      ? [...standaloneRoutes, ...operatorRoutes]
      : [
          {
            index: true,
            element: <Navigate replace to="/chat" />
          },
          ...routes
        ]

    return createBrowserRouter([
      {
        path: '/',
        element: (
          <Root>
            <ScrollRestoration />
            <Outlet />
          </Root>
        ),
        children: [
          // Identity-owned public routes: /auth/* (login, account recovery)
          // and /onboarding/* (post-login, pre-onboarding-complete). Only
          // mounted when identity is enabled — without an identity service
          // these routes have nothing to authenticate against.
          ...(identityEnabled ? [authenticationRoutes, onboardingRoutes] : []),
          // Everything else requires an authenticated, onboarded session when
          // identity is enabled; otherwise it renders directly with no guard.
          // Static import (unlike devops's lazy routes): identity is already
          // statically bundled via Root's OidcAuthProvider, and nx's
          // module-boundaries rule forbids mixing static + dynamic imports
          // of the same library.
          {
            element: identityEnabled ? (
              <AuthenticationGuard>
                <Outlet />
              </AuthenticationGuard>
            ) : (
              <Outlet />
            ),
            children: [...(identityEnabled ? [accountSettingsRoutes] : []), ...appRoutes]
          }
        ]
      }
    ])
  }, [technicianView, identityEnabled])

  return <RouterProvider router={router} />
}

// After a successful login, identity's PublicGuard redirects to this path.
// Technician boots a different router; keep its historical plant default.
setIdentityHomePath(getOperatorView() === 'technician' ? PLANT_HREF.root : '/chat')

const render = () => {
  const container = document.getElementById('root')
  if (!container) {
    throw new Error('Root element not found')
  }

  // Start Core Web Vitals collection (AXI-3875) before first paint so FCP/TTFB
  // are captured. No-op when perf telemetry is disabled or in a non-DOM context.
  initWebVitals()

  // Point @monaco-editor/react at the locally-bundled Monaco so the editor
  // never fetches loader.js from the jsdelivr CDN (blocked by our CSP).
  initializeMonacoEditor()

  createRoot(container).render(<App />)
}

export default render
